import { useState, useEffect, useMemo, useCallback } from 'react';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * ChannelDrawer — Edit a light controller channel.
 *
 * Form fields: channel name, color preset / RGB sliders, flashing toggle,
 * and device assignment list with search + "show selected only".
 *
 * Color behaviour varies by controller type:
 *   Type 1 (32ch):       White, Red, Green, Blue, Custom — full RGB editing
 *   Type 2 (96ch Mono):  White only — color selection hidden
 *   Type 3 (96ch RGB):   White, Red, Green, Blue — informational only, no Custom
 *
 * Props:
 *   open           — drawer visibility
 *   onClose        — close callback
 *   onSave         — save callback (receives payload object)
 *   saving         — boolean, disables buttons while saving
 *   editTarget     — channel record to edit (includes devices[], RGB values)
 *   controllerType — 1 | 2 | 3 (light_channel_type_id)
 *   devices        — full SNF device list for the campus
 *   controllerId   — light_controller_id of the parent controller
 *   campusId       — current campus ID
 */

// ---------------------------------------------------------------------------
// Color presets
// ---------------------------------------------------------------------------
const PRESETS = {
    White:  { r: 255, g: 255, b: 255 },
    Red:    { r: 255, g: 0,   b: 0   },
    Green:  { r: 0,   g: 255, b: 0   },
    Blue:   { r: 0,   g: 0,   b: 255 },
};

/** Detect which preset matches the given RGB, or 'Custom'. */
function detectPreset(r, g, b) {
    for (const [name, preset] of Object.entries(PRESETS)) {
        if (preset.r === r && preset.g === g && preset.b === b) return name;
    }
    return 'Custom';
}

/** Build preset options based on controller type. */
function getPresetOptions(controllerType) {
    switch (controllerType) {
        case 2:
            // 96ch Mono — white only, no selection needed
            return ['White'];
        case 3:
            // 96ch RGB — informational presets, no Custom
            return ['White', 'Red', 'Green', 'Blue'];
        case 1:
        default:
            // 32ch — full presets + custom RGB
            return ['White', 'Red', 'Green', 'Blue', 'Custom'];
    }
}

const namePattern = /^[a-zA-Z0-9_ \-/]*$/;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ChannelDrawer = ({ open, onClose, onSave, saving, editTarget, controllerType, devices, controllerId, campusId }) => {
    // Form state
    const [channelName, setChannelName] = useState('');
    const [preset, setPreset] = useState('White');
    const [red, setRed] = useState(255);
    const [green, setGreen] = useState(255);
    const [blue, setBlue] = useState(255);
    const [isFlashing, setIsFlashing] = useState(false);
    const [errors, setErrors] = useState({});

    // Device assignment state
    const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
    const [deviceSearch, setDeviceSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const presetOptions = useMemo(() => getPresetOptions(controllerType), [controllerType]);
    const showColorSection = controllerType !== 2;
    const allowCustom = controllerType === 1;

    // ======================================================================
    // Initialize form when drawer opens
    // ======================================================================
    useEffect(() => {
        if (open && editTarget) {
            setChannelName(editTarget.channel_name || '');

            const r = editTarget.red_intensity ?? 255;
            const g = editTarget.green_intensity ?? 255;
            const b = editTarget.blue_intensity ?? 255;
            setRed(r);
            setGreen(g);
            setBlue(b);

            const detected = detectPreset(r, g, b);
            // If detected is Custom but type doesn't support it, default to White
            if (detected === 'Custom' && !allowCustom) {
                setPreset('White');
            } else {
                setPreset(detected);
            }

            setIsFlashing(editTarget.is_flashing === 1);

            // Initialize device selection from nested devices array
            const ids = (editTarget.devices || []).map((d) => String(d.device_id));
            setSelectedDeviceIds(ids);

            setDeviceSearch('');
            setShowSelectedOnly(false);
            setErrors({});
        }
    }, [open, editTarget, allowCustom]);

    // ======================================================================
    // Color preset change
    // ======================================================================
    const handlePresetChange = (value) => {
        setPreset(value);
        if (value !== 'Custom' && PRESETS[value]) {
            setRed(PRESETS[value].r);
            setGreen(PRESETS[value].g);
            setBlue(PRESETS[value].b);
        }
    };

    // RGB slider changes → auto-select Custom
    const handleRedChange = (v) => {
        const val = Math.max(0, Math.min(255, parseInt(v) || 0));
        setRed(val);
        setPreset('Custom');
    };
    const handleGreenChange = (v) => {
        const val = Math.max(0, Math.min(255, parseInt(v) || 0));
        setGreen(val);
        setPreset('Custom');
    };
    const handleBlueChange = (v) => {
        const val = Math.max(0, Math.min(255, parseInt(v) || 0));
        setBlue(val);
        setPreset('Custom');
    };

    // ======================================================================
    // Device list
    // ======================================================================
    const filteredDevices = useMemo(() => {
        let list = [...(devices || [])];
        if (showSelectedOnly) {
            list = list.filter((d) => selectedDeviceIds.includes(String(d.device_id)));
        }
        if (deviceSearch.trim()) {
            const term = deviceSearch.toLowerCase();
            list = list.filter(
                (d) =>
                    (d.device_name || '').toLowerCase().includes(term) ||
                    (d.device_category || '').toLowerCase().includes(term) ||
                    (d.descriptions || '').toLowerCase().includes(term) ||
                    (d.manufacture_uuid || '').toLowerCase().includes(term) ||
                    (d.entity_name || '').toLowerCase().includes(term)
            );
        }
        list.sort((a, b) =>
            (a.device_name || '').localeCompare(b.device_name || '', undefined, { sensitivity: 'base' })
        );
        return list;
    }, [devices, deviceSearch, showSelectedOnly, selectedDeviceIds]);

    const handleDeviceToggle = useCallback((deviceId) => {
        const strId = String(deviceId);
        setSelectedDeviceIds((prev) =>
            prev.includes(strId) ? prev.filter((id) => id !== strId) : [...prev, strId]
        );
    }, []);

    const handleSelectAllDevices = useCallback(() => {
        const allFilteredIds = filteredDevices.map((d) => String(d.device_id));
        setSelectedDeviceIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
    }, [filteredDevices]);

    const handleClearDevices = useCallback(() => {
        setSelectedDeviceIds([]);
    }, []);

    // ======================================================================
    // Validation + Save
    // ======================================================================
    const clearError = (field) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const validate = () => {
        const errs = {};
        if (channelName.trim() && !namePattern.test(channelName.trim())) {
            errs.channelName = 'Only letters, numbers, spaces, and - _ / are allowed.';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const payload = [{
            light_relay_id: editTarget.light_relay_id,
            channel_name: channelName.trim(),
            is_flashing: isFlashing ? 1 : 0,
            red_intensity: red,
            green_intensity: green,
            blue_intensity: blue,
            device_ids: selectedDeviceIds,
            campus_id: campusId,
            light_controller_id: controllerId,
        }];

        onSave(payload);
    };

    // ======================================================================
    // Render
    // ======================================================================
    const colorPreviewStyle = {
        backgroundColor: `rgb(${red}, ${green}, ${blue})`,
    };

    const isCustom = preset === 'Custom';

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title="Edit Channel"
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save Channel'}
                        </button>
                    </>
                )}
            >
                {/* Channel # (read-only) */}
                {editTarget ? (
                    <p style={{ marginBottom: 'var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                        Channel: <strong>#{editTarget.channel_id}</strong>
                    </p>
                ) : null}

                {/* Channel Name */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">Channel Name</label>
                    <input
                        className={`dp-drawer__input${errors.channelName ? ' is-invalid' : ''}`}
                        type="text"
                        value={channelName}
                        onChange={(e) => { setChannelName(e.target.value); clearError('channelName'); }}
                        placeholder="e.g. Front Entrance"
                    />
                    {errors.channelName ? <p className="dp-drawer__error">{errors.channelName}</p> : null}
                </div>

                {/* Color Section — hidden for Type 2 (Mono) */}
                {showColorSection ? (
                    <>
                        {/* Info note for type 3 */}
                        {controllerType === 3 ? (
                            <p style={{ marginBottom: 'var(--space-3, 0.75rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontStyle: 'italic' }}>
                                Colors are informational only for record keeping with this style of controller.
                            </p>
                        ) : null}

                        {/* Color Preset Select */}
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">Color</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3, 0.75rem)' }}>
                                <select
                                    className="dp-drawer__input"
                                    value={isCustom && !allowCustom ? 'White' : preset}
                                    onChange={(e) => handlePresetChange(e.target.value)}
                                    style={{ flex: 1 }}
                                >
                                    {presetOptions.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                                <div className="lc-color-preview" style={colorPreviewStyle} aria-label={`Color preview: ${preset}`} />
                            </div>
                        </div>

                        {/* RGB Sliders — only for Custom on Type 1 */}
                        {allowCustom ? (
                            <div className="dp-drawer__field">
                                <label className="dp-drawer__label">RGB Values</label>

                                <div className="lc-rgb-field">
                                    <span className="lc-rgb-field__label" style={{ color: '#ef4444' }}>R</span>
                                    <input
                                        type="range"
                                        min={0}
                                        max={255}
                                        value={red}
                                        onChange={(e) => handleRedChange(e.target.value)}
                                        className="lc-rgb-field__slider"
                                        disabled={!isCustom}
                                    />
                                    <span className="lc-rgb-field__value">{red}</span>
                                </div>

                                <div className="lc-rgb-field">
                                    <span className="lc-rgb-field__label" style={{ color: '#22c55e' }}>G</span>
                                    <input
                                        type="range"
                                        min={0}
                                        max={255}
                                        value={green}
                                        onChange={(e) => handleGreenChange(e.target.value)}
                                        className="lc-rgb-field__slider"
                                        disabled={!isCustom}
                                    />
                                    <span className="lc-rgb-field__value">{green}</span>
                                </div>

                                <div className="lc-rgb-field">
                                    <span className="lc-rgb-field__label" style={{ color: '#3b82f6' }}>B</span>
                                    <input
                                        type="range"
                                        min={0}
                                        max={255}
                                        value={blue}
                                        onChange={(e) => handleBlueChange(e.target.value)}
                                        className="lc-rgb-field__slider"
                                        disabled={!isCustom}
                                    />
                                    <span className="lc-rgb-field__value">{blue}</span>
                                </div>

                                {!isCustom ? (
                                    <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                                        Select "Custom" to edit RGB values manually.
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <p style={{ marginBottom: 'var(--space-3, 0.75rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontStyle: 'italic' }}>
                        Monochrome controller — color is fixed to white.
                    </p>
                )}

                {/* Flashing Toggle */}
                <div className="ws-toggle-row">
                    <span className="ws-toggle-label">Flashing</span>
                    <label className="dp-toggle">
                        <input
                            type="checkbox"
                            checked={isFlashing}
                            onChange={(e) => setIsFlashing(e.target.checked)}
                        />
                        <span className="dp-toggle__track" />
                    </label>
                </div>

                {/* Device Assignment Section */}
                <div className="ws-device-picker" style={{ marginTop: 'var(--space-4, 1rem)' }}>
                    <div className="ws-device-picker__header">
                        <p className="dp-drawer__label" style={{ marginBottom: 0 }}>
                            Devices
                            <span className="ws-device-picker__count">
                                ({selectedDeviceIds.length} selected)
                            </span>
                        </p>
                    </div>

                    {/* Search */}
                    <div className="ws-device-picker__search">
                        <i className="fe fe-search ws-device-picker__search-icon" aria-hidden="true" />
                        <input
                            type="text"
                            placeholder="Search devices..."
                            value={deviceSearch}
                            onChange={(e) => setDeviceSearch(e.target.value)}
                            className="ws-device-picker__search-input"
                        />
                    </div>

                    {/* Select All / Clear / Show Selected */}
                    <div className="ws-device-picker__actions">
                        <button type="button" onClick={handleSelectAllDevices} className="ws-device-picker__action-btn">
                            Select All
                        </button>
                        <button type="button" onClick={handleClearDevices} className="ws-device-picker__action-btn">
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowSelectedOnly((prev) => !prev)}
                            className={`ws-device-picker__action-btn${showSelectedOnly ? ' ws-device-picker__action-btn--active' : ''}`}
                        >
                            Show Selected
                        </button>
                    </div>

                    {/* Device list */}
                    <div className="ws-device-picker__list">
                        {filteredDevices.length === 0 ? (
                            <p className="ws-device-picker__empty">
                                {showSelectedOnly ? 'No selections.' : 'No devices found'}
                            </p>
                        ) : (
                            filteredDevices.map((d) => {
                                const dId = String(d.device_id);
                                const isChecked = selectedDeviceIds.includes(dId);
                                return (
                                    <label
                                        key={dId}
                                        className={`ws-device-picker__item${isChecked ? ' is-selected' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleDeviceToggle(d.device_id)}
                                            className="ap-checkbox"
                                        />
                                        <div className="ws-device-picker__item-info">
                                            <span className="ws-device-picker__item-name">{d.device_name || '\u2014'}</span>
                                            <span className="ws-device-picker__item-desc">
                                                {[d.device_category, d.descriptions, d.manufacture_uuid, d.entity_name].filter(Boolean).join(' \u00b7 ')}
                                            </span>
                                        </div>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default ChannelDrawer;
